import {
	ChevronDown,
	ChevronRight,
	ExternalLink,
	Pin,
	Play,
	Plus,
	Search,
	Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../api/rest";
import {
	btn,
	ConfirmDialog,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtNumber,
	fmtRelative,
	input,
	Panel,
} from "../components/flows/ui";
import { PageHeader } from "../components/layout/PageHeader";
import {
	type BrandCategoryPageList,
	type BrandCategoryPageRow,
	type BrandOption,
	type CategoryOption,
	type PageAction,
	type PageRun,
	type PageStatus,
	type Result,
	storefrontUrl,
} from "../types/brand-category-pages";

type Tab = PageStatus | "all";

const TABS: { key: Tab; label: string }[] = [
	{ key: "active", label: "Active" },
	{ key: "candidate", label: "Candidates" },
	{ key: "retired", label: "Retired" },
	{ key: "all", label: "All" },
];

const DOT: Record<string, string> = {
	active: "bg-emerald-500",
	candidate: "bg-amber-400",
	retired: "bg-zinc-400",
	running: "bg-sky-500",
	done: "bg-emerald-500",
	failed: "bg-red-500",
	interrupted: "bg-zinc-400",
};

const POLL_MS = 4000;
const MIN_PRODUCTS = 10;

const Dot = ({ status, label }: { status: string; label?: string }) => (
	<span className="inline-flex items-center gap-1.5 whitespace-nowrap">
		<span className={`h-2 w-2 rounded-full ${DOT[status] ?? "bg-zinc-300"}`} />
		{label ?? status}
	</span>
);

const field = input.replace("w-full ", "");

const errorText = (err: unknown) =>
	err instanceof Error ? err.message : String(err);

export default function BrandCategoryPages() {
	const [tab, setTab] = useState<Tab>("active");
	const [q, setQ] = useState("");
	const [query, setQuery] = useState("");
	const [intro, setIntro] = useState("");
	const [sort, setSort] = useState("demand");
	const [page, setPage] = useState(1);
	const [data, setData] = useState<BrandCategoryPageList | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [openId, setOpenId] = useState<number | null>(null);
	const [runsVersion, setRunsVersion] = useState(0);

	const load = useCallback(async () => {
		try {
			const params = new URLSearchParams({
				page: String(page),
				limit: "50",
				sort,
			});
			if (tab !== "all") params.set("status", tab);
			if (query) params.set("q", query);
			if (intro) params.set("intro", intro);
			setData(
				await fetchJson<BrandCategoryPageList>(
					`/brand-category-pages?${params}`,
				),
			);
		} catch (err) {
			setError(errorText(err));
		}
	}, [tab, query, intro, sort, page]);

	useEffect(() => {
		load();
	}, [load]);

	const act = async (fn: () => Promise<Result>, done?: string) => {
		setError(null);
		setNotice(null);
		try {
			const res = await fn();
			if (!res.success) throw new Error(res.error);
			const problems = res.problems ?? [];
			setNotice([done, ...problems].filter(Boolean).join(" ") || null);
			await load();
		} catch (err) {
			setError(errorText(err));
		}
	};

	const setStatus = (row: BrandCategoryPageRow, action: PageAction) =>
		act(
			() =>
				sendJson<Result>(`/brand-category-pages/${row.id}`, "PATCH", {
					action,
				}),
			action === "release"
				? `${row.brandName} × ${row.categoryName} follows the nightly rules again.`
				: undefined,
		);

	const rewriteIntro = async (row: BrandCategoryPageRow) => {
		setError(null);
		const res = await sendJson<
			{ success: true; runId: number } | { success: false; error: string }
		>("/brand-category-pages/runs", "POST", { mode: "intros", ids: [row.id] });
		if (!res.success) {
			setError(res.error);
			return;
		}
		setNotice(
			`Writing a new intro for ${row.brandName} × ${row.categoryName}. It shows here when the run below finishes.`,
		);
		setRunsVersion((v) => v + 1);
	};

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Shop · Brand pages"
				title={`${fmtNumber(data?.counts.active ?? 0)} brand × category pages.`}
				qualifier="One brand in one category."
				description="Pages are proposed from search demand and need at least ten products in stock. Activating or retiring a page here pins it: the build keeps the pin until you release it."
			/>

			<RunPanel
				version={runsVersion}
				activeWithoutIntro={data?.activeWithoutIntro ?? 0}
				onFinished={load}
				onError={setError}
			/>

			<CreatePanel
				onDone={(message) => {
					setNotice(message);
					load();
				}}
				onError={setError}
			/>

			<div className="flex flex-wrap items-center gap-3">
				<div className="flex gap-1 rounded-md border border-hairline bg-white p-1">
					{TABS.map((t) => (
						<button
							key={t.key}
							type="button"
							onClick={() => {
								setTab(t.key);
								setPage(1);
							}}
							className={`rounded px-3 py-1.5 text-[13px] ${
								tab === t.key
									? "bg-primary text-white"
									: "text-nav hover:bg-canvas"
							}`}
						>
							{t.label}
							{t.key !== "all" && data
								? ` (${fmtNumber(data.counts[t.key])})`
								: ""}
						</button>
					))}
				</div>
				<form
					className="flex items-center gap-2"
					onSubmit={(e) => {
						e.preventDefault();
						setPage(1);
						setQuery(q.trim());
					}}
				>
					<input
						className={`${field} w-56`}
						placeholder="Brand, category or keyword"
						value={q}
						onChange={(e) => setQ(e.target.value)}
					/>
					<button type="submit" className={btn.ghost}>
						<Search className="h-4 w-4" />
						Search
					</button>
				</form>
				<select
					className={`${field} w-40`}
					value={intro}
					onChange={(e) => {
						setIntro(e.target.value);
						setPage(1);
					}}
				>
					<option value="">Any intro</option>
					<option value="missing">No intro</option>
					<option value="failed">Intro failed</option>
					<option value="written">Has intro</option>
					<option value="manual">Edited by hand</option>
				</select>
				<select
					className={`${field} w-40`}
					value={sort}
					onChange={(e) => {
						setSort(e.target.value);
						setPage(1);
					}}
				>
					<option value="demand">Most demand</option>
					<option value="products">Most products</option>
					<option value="recent">Recently changed</option>
					<option value="brand">Brand A–Z</option>
				</select>
			</div>

			<ErrorBanner error={error} />
			{notice && (
				<div className="rounded-xl border border-hairline bg-white px-4 py-3 text-[13px] text-slate-600">
					{notice}
				</div>
			)}

			<Panel>
				{data && data.items.length === 0 ? (
					<Empty>No pages here.</Empty>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-[13px]">
							<thead>
								<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
									<th className="py-2 pr-4 font-medium">Page</th>
									<th className="py-2 pr-4 font-medium">Demand</th>
									<th className="py-2 pl-3 text-right font-medium">Products</th>
									<th className="py-2 pl-4 font-medium">Intro</th>
									<th className="py-2 pl-3 font-medium">Status</th>
									<th className="py-2 pl-3 text-right font-medium">Actions</th>
								</tr>
							</thead>
							<tbody>
								{data?.items.map((row) => (
									<PageRows
										key={row.id}
										row={row}
										open={openId === row.id}
										onToggle={() =>
											setOpenId(openId === row.id ? null : row.id)
										}
										onStatus={(action) => setStatus(row, action)}
										onRewrite={() => rewriteIntro(row)}
										onSave={(text) =>
											act(
												() =>
													sendJson<Result>(
														`/brand-category-pages/${row.id}/intro`,
														"PUT",
														{ intro: text },
													),
												text.trim() ? "Intro saved." : "Intro cleared.",
											)
										}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
				{data && data.totalPages > 1 && (
					<div className="mt-4 flex items-center justify-end gap-2 text-[12px] text-slate-500">
						<button
							type="button"
							className={btn.ghost}
							disabled={page <= 1}
							onClick={() => setPage(page - 1)}
						>
							Previous
						</button>
						<span>
							Page {data.page} of {data.totalPages}
						</span>
						<button
							type="button"
							className={btn.ghost}
							disabled={page >= data.totalPages}
							onClick={() => setPage(page + 1)}
						>
							Next
						</button>
					</div>
				)}
			</Panel>
		</div>
	);
}

function PageRows({
	row,
	open,
	onToggle,
	onStatus,
	onRewrite,
	onSave,
}: {
	row: BrandCategoryPageRow;
	open: boolean;
	onToggle: () => void;
	onStatus: (action: PageAction) => void;
	onRewrite: () => void;
	onSave: (text: string) => void;
}) {
	const [draft, setDraft] = useState(row.intro ?? "");
	const [confirmRetire, setConfirmRetire] = useState(false);

	useEffect(() => {
		setDraft(row.intro ?? "");
	}, [row.intro]);

	const thin = row.productCount < MIN_PRODUCTS;
	return (
		<>
			<tr className="border-b border-hairline align-top">
				<td className="py-2 pr-4">
					<button
						type="button"
						onClick={onToggle}
						className="inline-flex items-start gap-1 text-left font-medium text-ink hover:underline"
					>
						{open ? (
							<ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						) : (
							<ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						)}
						{row.brandName} × {row.categoryName}
					</button>
					<div className="pl-4.5 text-[11px] text-slate-400">{row.path}</div>
				</td>
				<td className="py-2 pr-4 text-slate-600">
					{fmtNumber(row.demand)}
					<div className="text-[11px] text-slate-400">
						{row.demandSource}
						{row.demandKeyword ? ` · “${row.demandKeyword}”` : ""}
					</div>
				</td>
				<td
					className={`py-2 pl-3 text-right tabular-nums ${thin ? "text-slate-400" : ""}`}
					title={thin ? "Under ten products in stock at the last count" : ""}
				>
					{fmtNumber(row.productCount)}
				</td>
				<td className="max-w-xs py-2 pl-4 text-slate-600">
					{row.intro ? (
						<span className="line-clamp-2" title={row.intro}>
							{row.intro}
						</span>
					) : row.introError ? (
						<span className="line-clamp-2" title={row.introError}>
							<Dot status="failed" label="failed:" /> {row.introError}
						</span>
					) : (
						<span className="text-slate-400">none yet</span>
					)}
				</td>
				<td className="py-2 pl-3 text-slate-600">
					<Dot status={row.status} />
					{row.pinned && (
						<div
							className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-400"
							title={`Pinned ${fmtDateTime(row.pinnedAt)}`}
						>
							<Pin className="h-3 w-3" /> pinned
							{row.pinnedBy ? ` by ${row.pinnedBy.split("@")[0]}` : ""}
						</div>
					)}
				</td>
				<td className="py-2 pl-3">
					<div className="flex justify-end gap-1.5">
						{row.status !== "active" && (
							<button
								type="button"
								className={btn.ghost}
								onClick={() => onStatus("activate")}
							>
								Activate
							</button>
						)}
						{row.status !== "retired" && (
							<button
								type="button"
								className={btn.ghost}
								onClick={() =>
									row.status === "active"
										? setConfirmRetire(true)
										: onStatus("retire")
								}
							>
								Retire
							</button>
						)}
						{row.pinned && (
							<button
								type="button"
								className={btn.ghost}
								title="Let the nightly rules decide again"
								onClick={() => onStatus("release")}
							>
								Release pin
							</button>
						)}
						{row.status === "active" && (
							<a
								className={btn.ghost}
								href={storefrontUrl(row.path)}
								target="_blank"
								rel="noreferrer"
								title="Open on priceonline.dk"
							>
								<ExternalLink className="h-4 w-4" />
							</a>
						)}
					</div>
				</td>
			</tr>
			{open && (
				<tr className="border-b border-hairline bg-canvas/50">
					<td colSpan={6} className="px-4 py-4">
						<div className="grid gap-4 lg:grid-cols-3">
							<div className="space-y-2 lg:col-span-2">
								<div className="flex items-baseline justify-between">
									<h4 className="text-[12px] font-semibold text-slate-700">
										Intro
									</h4>
									<span className="text-[11px] text-slate-400">
										{row.intro
											? `${row.introModel === "manual" ? "Edited by hand" : `Written by ${row.introModel ?? "the model"}`} · ${fmtDateTime(row.introGeneratedAt)}`
											: `${row.introAttempts} attempt${row.introAttempts === 1 ? "" : "s"}`}
									</span>
								</div>
								<textarea
									className={`${input} min-h-[110px] resize-y bg-white`}
									value={draft}
									placeholder="No intro yet. The page shows only the heading and the products."
									onChange={(e) => setDraft(e.target.value)}
								/>
								<div className="flex flex-wrap gap-2">
									<button
										type="button"
										className={btn.ghost}
										disabled={draft === (row.intro ?? "")}
										onClick={() => onSave(draft)}
									>
										Save intro
									</button>
									<button
										type="button"
										className={btn.primary}
										disabled={row.status !== "active"}
										title={
											row.status === "active"
												? "Write a new intro with the model"
												: "Only an active page gets an intro"
										}
										onClick={onRewrite}
									>
										<Sparkles className="h-4 w-4" />
										{row.intro ? "Rewrite with AI" : "Write with AI"}
									</button>
									{row.intro && (
										<button
											type="button"
											className={btn.ghost}
											title="Clear it, so the next intro run writes a new one"
											onClick={() => onSave("")}
										>
											Clear
										</button>
									)}
								</div>
								{row.introError && (
									<p className="text-[12px] text-slate-500">
										<Dot status="failed" label="Last attempt failed:" />{" "}
										{row.introError}
									</p>
								)}
							</div>
							<dl className="grid grid-cols-2 gap-x-3 gap-y-1 self-start text-[12px] text-slate-600">
								<dt className="text-slate-400">Category</dt>
								<dd>{row.categoryPath}</dd>
								<dt className="text-slate-400">Panel volume</dt>
								<dd>{fmtNumber(row.panelVolume)}</dd>
								<dt className="text-slate-400">GSC impressions</dt>
								<dd>{fmtNumber(row.gscImpressions)}</dd>
								<dt className="text-slate-400">Activated</dt>
								<dd>{fmtDateTime(row.activatedAt)}</dd>
								<dt className="text-slate-400">Retired</dt>
								<dd>{fmtDateTime(row.retiredAt)}</dd>
								<dt className="text-slate-400">Last build</dt>
								<dd>{fmtDateTime(row.checkedAt)}</dd>
							</dl>
						</div>
					</td>
				</tr>
			)}
			{confirmRetire && (
				<ConfirmDialog
					title={`Retire ${row.brandName} × ${row.categoryName}?`}
					body={
						<>
							The page leaves the sitemap and stops being indexed. It stays
							retired until you activate it again or release the pin.
						</>
					}
					confirmLabel="Retire"
					danger
					onCancel={() => setConfirmRetire(false)}
					onConfirm={() => {
						setConfirmRetire(false);
						onStatus("retire");
					}}
				/>
			)}
		</>
	);
}

type RunStart =
	| { success: true; runId: number }
	| { success: false; error: string };

function RunPanel({
	version,
	activeWithoutIntro,
	onFinished,
	onError,
}: {
	version: number;
	activeWithoutIntro: number;
	onFinished: () => void;
	onError: (message: string | null) => void;
}) {
	const [runs, setRuns] = useState<PageRun[]>([]);
	const [maxActive, setMaxActive] = useState("");
	const [withIntros, setWithIntros] = useState(false);
	const [introLimit, setIntroLimit] = useState("50");
	const [dryRun, setDryRun] = useState(false);
	const [confirm, setConfirm] = useState<"build" | "intros" | null>(null);
	const [busy, setBusy] = useState(false);
	const [openRun, setOpenRun] = useState<PageRun | null>(null);

	const loadRuns = useCallback(async () => {
		try {
			setRuns(await fetchJson<PageRun[]>("/brand-category-pages/runs"));
		} catch (err) {
			onError(errorText(err));
		}
	}, [onError]);

	useEffect(() => {
		if (version >= 0) loadRuns();
	}, [loadRuns, version]);

	const running = runs.some((r) => r.status === "running");

	useEffect(() => {
		if (!running) return;
		const timer = setInterval(async () => {
			const before = runs
				.filter((r) => r.status === "running")
				.map((r) => r.id);
			const next = await fetchJson<PageRun[]>(
				"/brand-category-pages/runs",
			).catch(() => null);
			if (!next) return;
			setRuns(next);
			if (next.some((r) => before.includes(r.id) && r.status !== "running")) {
				onFinished();
			}
			if (openRun) {
				const fresh = await fetchJson<PageRun>(
					`/brand-category-pages/runs/${openRun.id}`,
				).catch(() => null);
				if (fresh) setOpenRun(fresh);
			}
		}, POLL_MS);
		return () => clearInterval(timer);
	}, [running, runs, openRun, onFinished]);

	const start = async (mode: "build" | "intros") => {
		setBusy(true);
		onError(null);
		const limit = Number(introLimit) || undefined;
		const body =
			mode === "build"
				? {
						mode,
						maxActive: Number(maxActive) || undefined,
						intros: withIntros,
						introLimit: withIntros ? limit : undefined,
						dryRun,
					}
				: { mode, introLimit: limit, dryRun };
		try {
			const res = await sendJson<RunStart>(
				"/brand-category-pages/runs",
				"POST",
				body,
			);
			if (!res.success) onError(res.error);
			setConfirm(null);
			await loadRuns();
		} catch (err) {
			onError(errorText(err));
		} finally {
			setBusy(false);
		}
	};

	const showRun = async (run: PageRun) => {
		if (openRun?.id === run.id) {
			setOpenRun(null);
			return;
		}
		setOpenRun(
			await fetchJson<PageRun>(`/brand-category-pages/runs/${run.id}`),
		);
	};

	const run = (mode: "build" | "intros") =>
		dryRun ? start(mode) : setConfirm(mode);

	return (
		<Panel
			title="Build and intros"
			hint="Runs the build_brand_category_pages job on the API server"
		>
			<div className="grid gap-5 lg:grid-cols-2">
				<div className="space-y-3">
					<p className="text-[12px] text-slate-500">
						<strong className="font-medium text-slate-700">Build</strong>{" "}
						recounts products and demand, activates every candidate that
						qualifies and retires pages that lost their demand or products. Max
						active caps the live pages, strongest demand first; leave it empty
						for no cap. Pinned pages are left as they are and do not count
						towards it. Takes a few minutes.
					</p>
					<div className="flex flex-wrap items-center gap-3 text-[13px]">
						<label className="flex items-center gap-2">
							Max active
							<input
								className={`${field} w-20`}
								inputMode="numeric"
								placeholder="all"
								value={maxActive}
								onChange={(e) => setMaxActive(e.target.value)}
							/>
						</label>
						<label className="flex items-center gap-2">
							<input
								type="checkbox"
								checked={withIntros}
								onChange={(e) => setWithIntros(e.target.checked)}
							/>
							Also write missing intros
						</label>
						<button
							type="button"
							className={btn.primary}
							disabled={busy || running}
							onClick={() => run("build")}
						>
							<Play className="h-4 w-4" /> Run build
						</button>
					</div>
				</div>
				<div className="space-y-3">
					<p className="text-[12px] text-slate-500">
						<strong className="font-medium text-slate-700">Intros</strong>{" "}
						writes the short text above the products for active pages that have
						none, with the Ollama model. {fmtNumber(activeWithoutIntro)} active
						page{activeWithoutIntro === 1 ? "" : "s"} have none. A page is tried
						three times at most.
					</p>
					<div className="flex flex-wrap items-center gap-3 text-[13px]">
						<label className="flex items-center gap-2">
							Pages
							<input
								className={`${field} w-20`}
								inputMode="numeric"
								value={introLimit}
								onChange={(e) => setIntroLimit(e.target.value)}
							/>
						</label>
						<button
							type="button"
							className={btn.primary}
							disabled={busy || running}
							onClick={() => run("intros")}
						>
							<Sparkles className="h-4 w-4" /> Write intros
						</button>
					</div>
				</div>
			</div>
			<label className="mt-3 flex items-center gap-2 text-[12px] text-slate-500">
				<input
					type="checkbox"
					checked={dryRun}
					onChange={(e) => setDryRun(e.target.checked)}
				/>
				Dry run: show what would change and write nothing
			</label>

			{runs.length > 0 && (
				<div className="mt-5 border-t border-hairline pt-4">
					<h4 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">
						Recent runs
					</h4>
					<ul className="space-y-1 text-[12px] text-slate-600">
						{runs.slice(0, 8).map((r) => (
							<li key={r.id}>
								<button
									type="button"
									className="flex w-full flex-wrap items-baseline gap-x-3 rounded px-1 py-0.5 text-left hover:bg-canvas"
									onClick={() => showRun(r)}
								>
									<Dot status={r.status} />
									<span className="font-medium text-slate-700">
										{r.mode}
										{r.dryRun ? " (dry run)" : ""}
									</span>
									<span className="text-slate-400">
										{fmtRelative(r.startedAt)}
										{r.requestedBy ? ` · ${r.requestedBy.split("@")[0]}` : ""}
									</span>
									<span className="min-w-0 flex-1 truncate">
										{r.summary ?? r.args}
									</span>
								</button>
								{openRun?.id === r.id && (
									<pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-zinc-950 p-3 text-[11px] leading-relaxed text-zinc-100">
										{openRun.args}
										{"\n\n"}
										{openRun.output || "No output yet."}
									</pre>
								)}
							</li>
						))}
					</ul>
				</div>
			)}

			{confirm && (
				<ConfirmDialog
					title={confirm === "build" ? "Run the build?" : "Write intros?"}
					body={
						confirm === "build" ? (
							<>
								{maxActive
									? `Up to ${maxActive} pages are kept active, strongest demand first.`
									: "Every page with demand and ten products in stock goes live."}{" "}
								Pages that stopped qualifying are retired and leave the sitemap.
								Pinned pages stay as they are.
								{withIntros &&
									` Then up to ${introLimit} intros are written with the model.`}
							</>
						) : (
							<>
								Up to {introLimit} active pages without an intro get one from
								the model. It uses the Ollama quota, so avoid running it while
								improve_products or the categorizer is running.
							</>
						)
					}
					confirmLabel={confirm === "build" ? "Run build" : "Write intros"}
					busy={busy}
					onCancel={() => setConfirm(null)}
					onConfirm={() => start(confirm)}
				/>
			)}
		</Panel>
	);
}

function CreatePanel({
	onDone,
	onError,
}: {
	onDone: (message: string) => void;
	onError: (message: string | null) => void;
}) {
	const [open, setOpen] = useState(false);
	const [term, setTerm] = useState("");
	const [brands, setBrands] = useState<BrandOption[]>([]);
	const [brand, setBrand] = useState<BrandOption | null>(null);
	const [categories, setCategories] = useState<CategoryOption[] | null>(null);
	const [busyId, setBusyId] = useState<number | null>(null);

	useEffect(() => {
		if (brand || term.trim().length < 2) {
			setBrands([]);
			return;
		}
		const timer = setTimeout(async () => {
			try {
				setBrands(
					await fetchJson<BrandOption[]>(
						`/brand-category-pages/brands?q=${encodeURIComponent(term.trim())}`,
					),
				);
			} catch (err) {
				onError(errorText(err));
			}
		}, 250);
		return () => clearTimeout(timer);
	}, [term, brand, onError]);

	const pick = async (option: BrandOption) => {
		setBrand(option);
		setTerm(option.name);
		setCategories(null);
		try {
			setCategories(
				await fetchJson<CategoryOption[]>(
					`/brand-category-pages/brands/${encodeURIComponent(option.slug)}/categories`,
				),
			);
		} catch (err) {
			onError(errorText(err));
		}
	};

	const create = async (category: CategoryOption) => {
		if (!brand) return;
		setBusyId(category.id);
		onError(null);
		try {
			const res = await sendJson<Result>("/brand-category-pages", "POST", {
				brandSlug: brand.slug,
				categoryId: category.id,
			});
			if (!res.success) throw new Error(res.error);
			onDone(
				[
					`${brand.name} × ${category.name} is live. Write its intro from the list below.`,
					...(res.problems ?? []),
				].join(" "),
			);
			await pick(brand);
		} catch (err) {
			onError(errorText(err));
		} finally {
			setBusyId(null);
		}
	};

	if (!open) {
		return (
			<button type="button" className={btn.ghost} onClick={() => setOpen(true)}>
				<Plus className="h-4 w-4" /> Create a page
			</button>
		);
	}

	return (
		<Panel
			title="Create a page"
			hint="Pick a brand, then one of the categories it has products in"
			action={
				<button
					type="button"
					className={btn.ghost}
					onClick={() => {
						setOpen(false);
						setBrand(null);
						setTerm("");
						setCategories(null);
					}}
				>
					Close
				</button>
			}
		>
			<div className="relative w-80">
				<input
					className={input}
					placeholder="Brand, e.g. Bosch"
					value={term}
					onChange={(e) => {
						setTerm(e.target.value);
						setBrand(null);
						setCategories(null);
					}}
				/>
				{brands.length > 0 && (
					<ul className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-hairline bg-white py-1 text-[13px] shadow-lg">
						{brands.map((b) => (
							<li key={b.slug}>
								<button
									type="button"
									className="flex w-full justify-between px-3 py-1.5 text-left hover:bg-canvas"
									onClick={() => pick(b)}
								>
									<span>{b.name}</span>
									<span className="text-slate-400">
										{fmtNumber(b.productCount)}
									</span>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>

			{brand && categories === null && (
				<p className="mt-3 text-[12px] text-slate-400">Counting products…</p>
			)}
			{brand && categories && categories.length === 0 && (
				<p className="mt-3 text-[12px] text-slate-500">
					{brand.name} has no products in stock in a category with a page.
				</p>
			)}
			{brand && categories && categories.length > 0 && (
				<div className="mt-4 max-h-96 overflow-auto">
					<table className="w-full text-[13px]">
						<thead>
							<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
								<th className="py-2 pr-4 font-medium">Category</th>
								<th className="py-2 pl-3 text-right font-medium">
									Products in stock
								</th>
								<th className="py-2 pl-4 font-medium">Page</th>
								<th className="py-2 pl-3" />
							</tr>
						</thead>
						<tbody>
							{categories.map((c) => (
								<tr key={c.id} className="border-b border-hairline">
									<td className="py-1.5 pr-4">
										{c.name}
										<div className="text-[11px] text-slate-400">{c.path}</div>
									</td>
									<td
										className={`py-1.5 pl-3 text-right tabular-nums ${c.products < MIN_PRODUCTS ? "text-slate-400" : ""}`}
									>
										{fmtNumber(c.products)}
									</td>
									<td className="py-1.5 pl-4 text-slate-600">
										{c.page ? (
											<Dot status={c.page.status} />
										) : (
											<span className="text-slate-400">none</span>
										)}
									</td>
									<td className="py-1.5 pl-3 text-right">
										{c.page?.status === "active" ? (
											<a
												className={btn.ghost}
												href={storefrontUrl(c.page.path)}
												target="_blank"
												rel="noreferrer"
											>
												<ExternalLink className="h-4 w-4" /> Open
											</a>
										) : (
											<button
												type="button"
												className={btn.ghost}
												disabled={busyId !== null}
												onClick={() => create(c)}
											>
												{c.page ? "Activate" : "Create"}
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</Panel>
	);
}
